import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Checkbox,
  SimpleGrid,
  Text,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
} from "@chakra-ui/react";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT } from "constants/brandColors";
import { getTranslatedField } from "utils/translations";

const BOT_CHANNELS_PAGE_ID = PAGE_IDS.BOT_CHANNELS;

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

function CreateBotChannel({ onCreated }) {
  const { t } = useTranslation();
  const [platform, setPlatform] = useState("TELEGRAM");
  const [targetType, setTargetType] = useState("CHANNEL");
  const [name, setName] = useState("");
  const [externalId, setExternalId] = useState("");
  const [isActive, setIsActive] = useState(true);
  // Schema defaults breakingOnly to true, but an editor adding a new channel
  // most likely wants it to receive all news, not just breaking — so the
  // dashboard defaults this checkbox unchecked and lets them opt in.
  const [breakingOnly, setBreakingOnly] = useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/category`);
        setCategories(Array.isArray(response) ? response : []);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
    fetchCategories();
  }, []);

  const toggleCategory = (categoryId) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  };

  const validate = () => {
    const errors = {};
    if (!name.trim()) errors.name = t("Name is required");
    if (!externalId.trim()) errors.externalId = t("External ID is required");
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const data = {
        page_id: BOT_CHANNELS_PAGE_ID,
        platform,
        targetType,
        name: name.trim(),
        externalId: externalId.trim(),
        isActive,
        breakingOnly,
        categoryIds: selectedCategoryIds,
      };
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/bot/target`,
        "POST",
        data
      );
      if (!response) {
        throw new Error("Failed to create channel");
      }
      setName("");
      setExternalId("");
      setIsActive(true);
      setBreakingOnly(false);
      setSelectedCategoryIds([]);
      onCreated?.();
    } catch (err) {
      // Duplicate (platform, externalId) comes back as a 400 with a specific
      // message — surface it as-is instead of a generic failure.
      setError(err.message || t("Failed to create channel. Please try again later."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormControl>
      <form onSubmit={handleSubmit}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Platform")}</FormLabel>
            <Select value={platform} onChange={(e) => setPlatform(e.target.value)} {...fieldProps}>
              <option value="TELEGRAM">{t("Telegram")}</option>
              <option value="WHATSAPP">{t("WhatsApp")}</option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Type")}</FormLabel>
            <Select value={targetType} onChange={(e) => setTargetType(e.target.value)} {...fieldProps}>
              <option value="CHANNEL">{t("Channel")}</option>
              <option value="GROUP">{t("Group")}</option>
            </Select>
          </FormControl>
        </SimpleGrid>

        <FormControl isInvalid={!!fieldErrors.name} mt="16px">
          <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Name")}</FormLabel>
          <Input
            isRequired
            placeholder={t("Display label shown in this dashboard")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            {...fieldProps}
          />
          <FormErrorMessage>{fieldErrors.name}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!fieldErrors.externalId} mt="16px">
          <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("External ID")}</FormLabel>
          <Input
            isRequired
            placeholder={t("Telegram @username / chat id, or WhatsApp group JID (1234567890@g.us)")}
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
            {...fieldProps}
          />
          <FormErrorMessage>{fieldErrors.externalId}</FormErrorMessage>
        </FormControl>

        <Box mt="16px">
          <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Categories")}</FormLabel>
          <Text fontSize="xs" color="gray.500" mb="8px">
            {t("Leave empty to send news from all categories to this channel.")}
          </Text>
          {categories.length === 0 ? (
            <Text fontSize="sm" color="gray.500">{t("No categories yet.")}</Text>
          ) : (
            <Wrap>
              {categories.map((category) => {
                const isSelected = selectedCategoryIds.includes(category.id);
                return (
                  <WrapItem key={category.id}>
                    <Tag
                      as="button"
                      type="button"
                      onClick={() => toggleCategory(category.id)}
                      bg={isSelected ? GOLD_BORDER : "gray.100"}
                      color={isSelected ? "#1A1A1A" : "gray.700"}
                      cursor="pointer"
                      borderRadius="full"
                    >
                      <TagLabel>{getTranslatedField(category.translations, "en")}</TagLabel>
                    </Tag>
                  </WrapItem>
                );
              })}
            </Wrap>
          )}
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt="16px">
          <Checkbox isChecked={isActive} onChange={(e) => setIsActive(e.target.checked)} colorScheme="brand">
            {t("Active")}
          </Checkbox>
          <Checkbox isChecked={breakingOnly} onChange={(e) => setBreakingOnly(e.target.checked)} colorScheme="brand">
            {t("Breaking news only")}
          </Checkbox>
        </SimpleGrid>

        <Button
          type="submit"
          fontSize="sm"
          bg={GOLD_BORDER}
          color="#1A1A1A"
          _hover={{ bg: GOLD_TEXT, color: "white" }}
          _active={{ bg: GOLD_TEXT, color: "white" }}
          fontWeight="500"
          w="100%"
          h="50px"
          mt="20px"
          mb="8px"
          isLoading={isSubmitting}
        >
          {t("Create Channel")}
        </Button>
        {error && (
          <Box color="red.500" mt={2} whiteSpace="pre-line">
            {error}
          </Box>
        )}
      </form>
    </FormControl>
  );
}

export default CreateBotChannel;
